import {Controller} from '@hotwired/stimulus';

/**
 * Champ à liste de suggestions du design system (`combobox.md`), sur le modèle « combobox » de l'APG.
 * Flèches pour parcourir, Entrée pour choisir, Échap pour fermer sans changer la valeur.
 *
 * Deux sources d'options :
 * - `url` : dès `minLength` caractères saisis, la liste se remplit depuis `url` (GET `?q=`, réponse JSON
 *   `[{value, label}]`) ;
 * - `options` : liste fournie par la page (`[{value, label, path, level, empty}]`), filtrée sur place sans tenir
 *   compte des accents ni de la casse ; la liste entière s'ouvre au clic ou aux flèches, décalée par niveau, et un
 *   filtre affiche les catégories parentes (le début de `path`) devant le nom des options trouvées et retire l'option
 *   vide (`empty`).
 *
 * La liste s'ouvre au clic ou à la frappe, jamais au seul focus. Le libellé `noResult` peut citer la saisie par
 * `{query}`. Au focus, le texte du champ est sélectionné : la frappe remplace l'option choisie.
 *
 * Choix unique : choisir une option, ou modifier le texte d'une option choisie, émet `combobox:change`
 * (`detail: {value, label, name, created}`, `value` vide quand le texte ne nomme plus l'option ; `label` est le texte
 * du champ, chemin compris, `name` le seul nom de l'option, `created` vrai pour une catégorie à créer) et renseigne la target `value`
 * si elle existe (champ caché posté avec le formulaire). Une target `value` déjà remplie au chargement vaut option
 * choisie, avec le texte du champ pour libellé.
 *
 * Avec `restore`, la frappe ne désélectionne rien : Échap ou la sortie du champ sans choix rendent au champ le texte
 * de l'option choisie, dont la valeur ne change pas.
 *
 * Avec `createField` (id d'un champ caché du formulaire), une saisie qui ne nomme exactement aucune option propose
 * en dernière ligne `labels.create` (« Créer la catégorie « {query} » ») ; sans résultat, `labels.createNoResult`
 * la précède. La choisir inscrit la saisie dans ce champ, choisit l'option vide et affiche `labels.created`
 * (« {label} (nouvelle) ») : rien n'est créé avant l'enregistrement du formulaire.
 *
 * Choix multiple (`multiple`) : chaque choix devient un `tag` de la target `tags`, qui porte son champ caché
 * `name` ; choisir à nouveau une option, son bouton « × » (action `remove`) ou Retour arrière dans un champ vide
 * la retire. Chaque ajout ou retrait émet `combobox:change` (`detail: {values}`).
 *
 *   <div {{ stimulus_controller('combobox', {url: path('…'), labels: {noResult: '…'|trans, error: '…'|trans}}) }}>
 *     <label id="xLabel" for="x" class="form-label">…</label>
 *     <div class="combobox">
 *       <input type="text" id="x" class="form-control" role="combobox" aria-autocomplete="list"
 *              aria-expanded="false" aria-controls="xOptions" autocomplete="off" data-combobox-target="input">
 *       <span class="combobox-chevron" aria-hidden="true">…</span>
 *       <ul id="xOptions" class="combobox-list" role="listbox" aria-labelledby="xLabel" hidden
 *           data-combobox-target="list"></ul>
 *     </div>
 *   </div>
 *
 * Échap qui ferme la liste ne remonte pas au-delà du champ : une fenêtre qui l'écoute ne se ferme pas avec.
 * Un autre contrôleur peut le piloter par un outlet `combobox` : `focus()` place le focus dans le champ,
 * `clear()` le vide (et émet `combobox:change` si une option était choisie) puis y place le focus.
 */
export default class extends Controller {
  static targets = ['input', 'list', 'value', 'tags'];

  static values = {
    url: String,
    options: Array,
    multiple: Boolean,
    name: String,
    minLength: {type: Number, default: 3},
    delay: {type: Number, default: 250},
    labels: Object,
    restore: Boolean,
    createField: String,
  };

  initialize() {
    this.search = this.search.bind(this);
    this.browse = this.browse.bind(this);
    this.keydown = this.keydown.bind(this);
    this.keyup = this.keyup.bind(this);
    this.close = this.close.bind(this);
    this.focused = this.focused.bind(this);
    this.left = this.left.bind(this);
    this.pressed = this.pressed.bind(this);
    this.clicked = this.clicked.bind(this);
    this.hold = this.hold.bind(this);
    this.pick = this.pick.bind(this);
  }

  connect() {
    this.options = [];
    this.active = -1;
    this.selected = null;

    if (this.hasValueTarget && this.valueTarget.value !== '') {
      this.selected = {value: this.valueTarget.value, label: this.inputTarget.value};
    }

    // une création choisie avant un envoi refusé reste affichée
    const created = this.#createInput()?.value ?? '';

    if (created !== '' && this.selected !== null) {
      this.inputTarget.value = this.#createdLabel(created);
      this.selected = {...this.selected, label: this.inputTarget.value, created};
    }

    this.inputTarget.addEventListener('input', this.search);
    this.inputTarget.addEventListener('mousedown', this.pressed);
    this.inputTarget.addEventListener('focus', this.focused);
    // un clic dans le champ déjà actif rouvre la liste fermée par Échap ou par un choix
    this.inputTarget.addEventListener('click', this.clicked);
    this.inputTarget.addEventListener('keydown', this.keydown);
    this.inputTarget.addEventListener('keyup', this.keyup);
    this.inputTarget.addEventListener('blur', this.left);
    this.listTarget.addEventListener('mousedown', this.hold);
    this.listTarget.addEventListener('click', this.pick);
  }

  disconnect() {
    clearTimeout(this.timer);
    this.request?.abort();

    this.inputTarget.removeEventListener('input', this.search);
    this.inputTarget.removeEventListener('mousedown', this.pressed);
    this.inputTarget.removeEventListener('focus', this.focused);
    this.inputTarget.removeEventListener('click', this.clicked);
    this.inputTarget.removeEventListener('keydown', this.keydown);
    this.inputTarget.removeEventListener('keyup', this.keyup);
    this.inputTarget.removeEventListener('blur', this.left);
    this.listTarget.removeEventListener('mousedown', this.hold);
    this.listTarget.removeEventListener('click', this.pick);
  }

  search() {
    clearTimeout(this.timer);
    this.request?.abort();

    const query = this.inputTarget.value.trim();

    // un texte qui ne nomme plus l'option choisie la désélectionne : le champ caché ne garde pas l'ancienne valeur
    if (!this.multipleValue && !this.restoreValue && this.selected !== null && query !== this.selected.label.trim()) {
      this.#select(null);
    }

    if (this.#isLocal()) {
      this.#filter(query);
      return;
    }

    if (query.length < this.minLengthValue) {
      this.options = [];
      this.listTarget.replaceChildren();
      this.close();
      return;
    }

    this.timer = setTimeout(() => this.#fetch(query), this.delayValue);
  }

  focused() {
    this.inputTarget.select();
  }

  left() {
    this.close();
    this.#restore();
  }

  // le clic qui donne le focus pose le curseur au relâcher, et déferait la sélection faite au focus
  pressed() {
    this.selectOnClick = document.activeElement !== this.inputTarget;
  }

  clicked() {
    if (this.selectOnClick) {
      this.inputTarget.select();
      this.selectOnClick = false;
    }

    this.browse();
  }

  // options locales : la liste entière s'ouvre au clic
  browse() {
    if (this.#isLocal()) {
      this.#filter(this.inputTarget.value.trim());
    }
  }

  keydown(event) {
    const open = !this.listTarget.hidden;
    this.selectOnClick = false;

    switch (event.key) {
    case 'ArrowDown':
    case 'ArrowUp': {
      if (this.#isLocal() && !open) {
        this.#filter(this.inputTarget.value.trim());
      }

      if (this.options.length === 0) {
        return;
      }

      event.preventDefault();
      this.#open();

      const step = event.key === 'ArrowDown' ? 1 : -1;
      const count = this.options.length;
      const start = step > 0 ? 0 : count - 1;
      this.#activate(this.active === -1 ? start : (this.active + step + count) % count);
      break;
    }
    case 'Enter':
      if (open) {
        event.preventDefault();

        if (this.active !== -1) {
          this.#choose(this.active);
        }
      }
      break;
    case 'Escape':
      if (open) {
        event.preventDefault();
        event.stopPropagation();
        this.escaped = true;
        this.close();

        // le texte rendu reste sélectionné : la frappe suivante le remplace
        if (this.#restore()) {
          this.inputTarget.select();
        }
      }
      break;
    case 'Backspace':
      if (this.multipleValue && this.inputTarget.value === '') {
        const tags = this.#tags();

        if (tags.length > 0) {
          this.#removeTag(tags[tags.length - 1]);
        }
      }
      break;
    }
  }

  keyup(event) {
    if (event.key === 'Escape' && this.escaped) {
      event.stopPropagation();
      this.escaped = false;
    }
  }

  focus() {
    this.inputTarget.focus();
    this.browse();
  }

  clear() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.inputTarget.value = '';
    this.options = [];
    this.listTarget.replaceChildren();
    this.close();

    if (this.selected !== null) {
      this.#select(null);
    }

    this.focus();
  }

  close() {
    this.selectOnClick = false;
    this.listTarget.hidden = true;
    this.inputTarget.setAttribute('aria-expanded', 'false');
    this.inputTarget.removeAttribute('aria-activedescendant');
    this.active = -1;
  }

  remove(event) {
    this.#removeTag(event.currentTarget.closest('[data-value]'));
    this.focus();
  }

  // garde le focus dans le champ : sans cela, sa perte fermerait la liste avant le clic sur l'option
  hold(event) {
    event.preventDefault();
  }

  pick(event) {
    const option = event.target.closest('[role="option"]:not([aria-disabled="true"])');

    if (option) {
      this.#choose(Number(option.dataset.index));
    }
  }

  #isLocal() {
    return this.hasOptionsValue;
  }

  // le texte d'une option choisie (choix unique) ne filtre pas : la liste entière reste proposée
  #filter(query) {
    const shown = this.selected !== null && query === this.selected.label.trim() ? '' : query;
    const needle = fold(shown);

    this.options = needle === '' ?
      this.optionsValue :
      this.optionsValue.filter((option) => !option.empty && fold(option.label).includes(needle));

    const creatable = needle !== '' && this.#createInput() !== null &&
      !this.optionsValue.some((option) => !option.empty && fold(option.label) === needle);
    const found = this.options.length > 0;

    if (creatable) {
      this.options = [...this.options, {create: shown}];
    }

    this.#render(shown, found ? null : this.#noResult(shown, creatable));

    if (document.activeElement === this.inputTarget) {
      this.#open();
    }
  }

  async #fetch(query) {
    const request = new AbortController();
    this.request = request;

    const url = new URL(this.urlValue, document.baseURI);
    url.searchParams.set('q', query);

    let message = null;

    try {
      const response = await fetch(url, {
        signal: request.signal,
        headers: {'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      this.options = await response.json();
    } catch (error) {
      if (request.signal.aborted) {
        return;
      }

      this.options = [];
      message = this.labelsValue.error ?? '';
    }

    // une frappe plus récente a relancé la recherche
    if (this.request !== request) {
      return;
    }

    this.#render(query, message ?? (this.options.length === 0 ? this.#noResult(query) : null));

    if (document.activeElement === this.inputTarget) {
      this.#open();
    }
  }

  #render(query, message) {
    this.active = -1;
    this.inputTarget.removeAttribute('aria-activedescendant');

    const empty = [];

    if (message !== null) {
      const item = document.createElement('li');
      item.className = 'combobox-empty';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-disabled', 'true');
      item.textContent = message;
      empty.push(item);
    }

    // sans filtre, l'arbre se lit au retrait ; filtrées, les options disent leur chemin
    const browsing = query === '';
    const chosen = this.#chosenValues();

    this.listTarget.replaceChildren(...empty, ...this.options.map((option, index) => {
      const item = document.createElement('li');
      item.id = `${this.listTarget.id}-${index}`;
      item.className = 'combobox-option';
      item.dataset.index = String(index);
      item.setAttribute('role', 'option');

      if (option.create !== undefined) {
        item.classList.add('combobox-create');
        item.textContent = (this.labelsValue.create ?? '').replace('{query}', () => option.create);
        return item;
      }

      item.setAttribute('aria-selected', String(chosen.has(String(option.value))));

      if (option.path !== undefined) {
        item.setAttribute('aria-label', option.path);
      }

      if (browsing && option.level !== undefined) {
        item.dataset.level = String(option.level);
      }

      const label = document.createElement('span');

      // le chemin finit par le nom : ce qui le précède nomme les catégories parentes, séparateur compris
      const parents = option.path?.endsWith(option.label) ? option.path.slice(0, -option.label.length) : '';

      if (!browsing && parents !== '') {
        const parent = document.createElement('span');
        parent.className = 'combobox-path';
        parent.textContent = parents;
        label.append(parent);
      }

      label.append(...highlight(option.label, query));
      item.append(label);
      return item;
    }));
  }

  #noResult(query, creatable = false) {
    const label = creatable ? this.labelsValue.createNoResult : this.labelsValue.noResult;

    return (label ?? '').replace('{query}', () => query);
  }

  #open() {
    if (this.listTarget.childElementCount === 0) {
      return;
    }

    this.listTarget.hidden = false;
    this.inputTarget.setAttribute('aria-expanded', 'true');
  }

  #activate(index) {
    this.active = index;

    const items = [...this.listTarget.querySelectorAll('.combobox-option')];
    items.forEach((item, position) => item.classList.toggle('is-active', position === index));

    const item = items[index];
    this.inputTarget.setAttribute('aria-activedescendant', item.id);
    item.scrollIntoView({block: 'nearest'});
  }

  #choose(index) {
    const option = this.options[index];

    if (this.multipleValue) {
      const tag = this.#tags().find((element) => element.dataset.value === String(option.value));

      if (tag) {
        this.#removeTag(tag);
      } else {
        this.#addTag(option);
      }

      this.inputTarget.value = '';
      this.#filter('');
      return;
    }

    const createInput = this.#createInput();

    if (option.create !== undefined) {
      const none = this.optionsValue.find((candidate) => candidate.empty);
      createInput.value = option.create;
      this.inputTarget.value = this.#createdLabel(option.create);
      this.close();
      this.#select({value: none?.value ?? '', label: this.inputTarget.value, name: this.inputTarget.value, created: option.create});
      return;
    }

    if (createInput !== null) {
      createInput.value = '';
    }

    const text = option.path ?? option.label;
    this.inputTarget.value = text;
    this.close();
    this.#select({...option, label: text, name: option.label});
  }

  // la valeur ne change pas : le champ reprend le texte de l'option choisie
  #restore() {
    if (!this.restoreValue || this.multipleValue || this.selected === null || this.inputTarget.value === this.selected.label) {
      return false;
    }

    this.inputTarget.value = this.selected.label;
    return true;
  }

  #createInput() {
    return this.hasCreateFieldValue ? document.getElementById(this.createFieldValue) : null;
  }

  #createdLabel(name) {
    return (this.labelsValue.created ?? '{label}').replace('{label}', () => name);
  }

  #select(option) {
    this.selected = option;

    if (this.hasValueTarget) {
      this.valueTarget.value = option?.value ?? '';
    }

    this.dispatch('change', {detail: {
      value: option?.value ?? '',
      label: option?.label ?? '',
      name: option?.name ?? option?.label ?? '',
      created: Boolean(option?.created),
    }});
  }

  #chosenValues() {
    if (this.multipleValue) {
      return new Set(this.#tags().map((tag) => tag.dataset.value));
    }

    return new Set(this.selected === null || this.selected.created ? [] : [String(this.selected.value)]);
  }

  #tags() {
    return this.hasTagsTarget ? [...this.tagsTarget.querySelectorAll('[data-value]')] : [];
  }

  #addTag(option) {
    const text = option.path ?? option.label;
    const removeLabel = (this.labelsValue.remove ?? '').replace('{label}', () => text);

    const tag = document.createElement('span');
    tag.className = 'tag';
    tag.dataset.value = String(option.value);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'tag-remove';
    button.title = removeLabel;
    button.setAttribute('aria-label', removeLabel);
    button.dataset.action = `${this.identifier}#remove`;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = this.nameValue;
    input.value = String(option.value);

    tag.append(document.createTextNode(text), button, input);
    this.tagsTarget.append(tag);
    this.#changed();
  }

  #removeTag(tag) {
    tag.remove();
    this.#changed();

    if (!this.listTarget.hidden) {
      this.#filter(this.inputTarget.value.trim());
    }
  }

  #changed() {
    this.dispatch('change', {detail: {values: this.#tags().map((tag) => tag.dataset.value)}});
  }
}

/**
 * Texte sans accents ni casse, pour comparer une saisie à un libellé.
 */
function fold(text) {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/**
 * Libellé d'option dont la partie saisie est mise en valeur, sans tenir compte de la casse ni des accents.
 */
function highlight(label, query) {
  const chars = Array.from(label);
  const starts = [];
  let folded = '';

  chars.forEach((char) => {
    starts.push(folded.length);
    folded += fold(char);
  });

  const needle = fold(query);
  const at = needle === '' ? -1 : folded.indexOf(needle);

  if (at === -1) {
    return [document.createTextNode(label)];
  }

  const from = starts.findIndex((start) => start >= at);
  const until = starts.findIndex((start) => start >= at + needle.length);
  const to = until === -1 ? chars.length : until;

  const match = document.createElement('span');
  match.className = 'combobox-match';
  match.textContent = chars.slice(from, to).join('');

  return [
    document.createTextNode(chars.slice(0, from).join('')),
    match,
    document.createTextNode(chars.slice(to).join('')),
  ];
}
